import { Button, Text } from '@/ui-kit'
import { clsx } from 'clsx'
import { ConnectionState } from 'livekit-client'
import { CheckCircle, Mic, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useConversationLogger } from '../../hooks/useConversationLogger'
import type { LiveKitParticipant } from '../../hooks/useLiveKit'
import { ParticipantCircles } from './ParticipantCircles'
import { RecordingCountdown } from './RecordingStartCountdown'

interface SessionLobbyProps {
  requiredCount: number
  readyCount: number
  isReady: boolean
  topic?: string
  instruction?: string
  onReady: () => void | Promise<void>
  onLeave: () => void | Promise<void>
  connectionState: ConnectionState
  participants: LiveKitParticipant[]
  isMuted: boolean
  toggleMute: () => void
  hasLiveKit: boolean
  recordingStartsAt: string | null
  onCountdownComplete: () => void
}

export function SessionLobby({
  requiredCount,
  readyCount,
  isReady,
  topic,
  instruction,
  onReady,
  onLeave,
  connectionState,
  participants,
  isMuted,
  toggleMute,
  hasLiveKit,
  recordingStartsAt,
  onCountdownComplete
}: SessionLobbyProps) {
  const log = useConversationLogger()
  const [readyLoading, setReadyLoading] = useState(false)
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null)
  const countdownCompleteRef = useRef(onCountdownComplete)
  countdownCompleteRef.current = onCountdownComplete

  useEffect(() => {
    if (!recordingStartsAt) {
      setCountdownSeconds(null)
      return
    }

    const target = new Date(recordingStartsAt).getTime()

    const tick = () => {
      const remaining = Math.ceil((target - Date.now()) / 1000)
      if (remaining <= 0) {
        setCountdownSeconds(0)
        countdownCompleteRef.current()
        return
      }
      setCountdownSeconds(remaining)
    }

    tick()
    const interval = setInterval(tick, 200)
    return () => clearInterval(interval)
  }, [recordingStartsAt])

  const isCountingDown = countdownSeconds !== null && countdownSeconds > 0

  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    if (hasLiveKit) return

    let cancelled = false
    log('mic', 'Requesting microphone access (local fallback)')

    navigator.mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      })
      .then((stream) => {
        if (cancelled) {
          for (const t of stream.getTracks()) t.stop()
          return
        }
        streamRef.current = stream
        log('mic', 'Microphone access granted (local)')
      })
      .catch((err) => {
        if (!cancelled) {
          log('error', `Microphone access denied: ${err.message}`)
          toast.error('Microphone access is required for voice conversations')
        }
      })

    return () => {
      cancelled = true
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop()
      }
    }
  }, [hasLiveKit, log])

  const handleReady = async () => {
    setReadyLoading(true)
    try {
      await onReady()
    } finally {
      setReadyLoading(false)
    }
  }

  const livekitConnected = connectionState === ConnectionState.Connected

  const micReady = hasLiveKit ? livekitConnected : !!streamRef.current

  return (
    <div style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      <div className="flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        {instruction && (
          <Text
            type="body2-regular"
            className="text-gray-90 m-auto block rounded-xl border border-solid border-gray-100 p-2 text-center"
          >
            {instruction}
          </Text>
        )}
        {topic && (
          <span className="text-h5 block text-center leading-[120%] whitespace-pre-line">&ldquo;{topic}&rdquo;</span>
        )}

        {isCountingDown ? (
          <RecordingCountdown countdownSeconds={countdownSeconds} />
        ) : hasLiveKit ? (
          <>
            <ParticipantCircles
              participants={participants}
              isMuted={isMuted}
              toggleMute={toggleMute}
              lobbyMode={{ readyCount, requiredCount }}
            />

            <div className="flex items-center gap-2">
              {isReady ? (
                <>
                  <span className="bg-orange size-2.5 animate-pulse rounded-full" />
                  <Text type="body2-regular" className="text-gray-90">
                    Waiting for others to be ready...
                  </Text>
                </>
              ) : (
                <Text type="body2-regular" className="text-gray-90">
                  {readyCount}/{requiredCount} ready
                </Text>
              )}
            </div>

            <div className="flex flex-col items-center gap-3">
              {!isReady && (
                <Button
                  variant="green"
                  onClick={handleReady}
                  isDisabled={!livekitConnected}
                  isLoading={readyLoading}
                  className="gap-2"
                >
                  <CheckCircle className="size-5" />
                  I&apos;m Ready
                </Button>
              )}

              {isReady && (
                <div className="bg-green/10 text-green flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
                  <CheckCircle className="size-4" />
                  You&apos;re ready
                </div>
              )}

              <Button variant="bordered-gray" size="small" onClick={onLeave} className="gap-1.5">
                <X className="size-4" />
                Leave
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2">
              <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
                {micReady && (
                  <>
                    <div
                      className="bg-green absolute inset-0 rounded-full"
                      style={{ animation: 'nectar-pulse-ring 2s ease-out infinite' }}
                    />
                    <div
                      className="bg-green absolute inset-0 rounded-full opacity-0"
                      style={{ animation: 'nectar-pulse-ring 2s ease-out infinite 0.8s' }}
                    />
                  </>
                )}
                <div
                  className={clsx(
                    'relative z-10 flex size-20 items-center justify-center rounded-full text-white shadow-lg',
                    micReady ? 'bg-green' : 'bg-gray-80 animate-pulse'
                  )}
                >
                  <Mic className="size-8" />
                </div>
              </div>
              <Text type="body2-regular" className="text-gray-90">
                {micReady ? 'Microphone ready' : 'Requesting microphone access...'}
              </Text>
            </div>

            <div className="flex items-center gap-2">
              <span className="bg-orange size-2.5 animate-pulse rounded-full" />
              <Text type="body2-regular" className="text-gray-90">
                Waiting for participants to join...
              </Text>
            </div>

            <Button variant="bordered-gray" size="small" onClick={onLeave} className="gap-1.5">
              <X className="size-4" />
              Leave
            </Button>
          </>
        )}

        <Text type="body3-regular" className="text-gray-80 text-center">
          {isCountingDown
            ? 'Get ready to start your conversation!'
            : hasLiveKit
              ? 'Recording will begin after a short countdown once all participants are ready.'
              : "You'll be able to talk and signal ready once everyone joins."}
          {!isCountingDown && topic && ' Review the topic above while you wait.'}
        </Text>
      </div>
    </div>
  )
}
